import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { CaptionOptionsRead } from '../../types/api';
import { fetchCaptionOptions } from './generationApi';

/**
 * The caption options registry, loaded once per session.
 *
 * Cached in a module-level promise exactly like `useIntents`: the brief and
 * the review spec sheet can both ask, and that is one request. A failed load
 * clears the cache, so the next mount really retries.
 */
let optionsPromise: Promise<CaptionOptionsRead> | null = null;

function loadCaptionOptions(): Promise<CaptionOptionsRead> {
  if (optionsPromise === null) {
    optionsPromise = fetchCaptionOptions().catch((caught: unknown) => {
      optionsPromise = null;
      throw caught;
    });
  }
  return optionsPromise;
}

export interface CaptionOptionsResult {
  /** The registry, or null while loading and on failure. */
  options: CaptionOptionsRead | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Labels, blurbs and per-platform length ranges for the caption controls.
 *
 * A failure is never fatal: both controls are optional and their keys are the
 * wire vocabulary, so the chips still work without the registry. What is lost
 * is the numbers under the Length chips and the Style blurbs, and the brief
 * says so rather than blocking.
 */
export function useCaptionOptions(): CaptionOptionsResult {
  const [options, setOptions] = useState<CaptionOptionsRead | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const loaded = await loadCaptionOptions();
        if (cancelled) return;
        setOptions(loaded);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setError(getErrorMessage(caught, 'Could not load the caption options.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { options, isLoading: options === null && error === null, error };
}
